import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import ProtectedRoute from "@/components/ProtectedRoute";

// Público: carga inmediata (ruta crítica para clientes que reservan en línea)
import Landing from "@/pages/public/Landing";
import Book from "@/pages/public/Book";
import BookingSuccess from "@/pages/public/BookingSuccess";
import Reschedule from "@/pages/public/Reschedule";
import Wallet from "@/pages/public/Wallet";
import Privacy from "@/pages/public/Privacy";
import Terms from "@/pages/public/Terms";
import TestimonialPage from "@/pages/public/Testimonial";
import Login from "@/pages/Login";

// Cuenta de cliente (bajo demanda)
const MiCuenta = lazy(() => import("@/pages/client/MiCuenta"));
const Referir = lazy(() => import("@/pages/client/Referir"));

// Admin (bajo demanda: solo se descarga al entrar al panel)
const AdminLayout = lazy(() => import("@/components/AdminLayout"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const Services = lazy(() => import("@/pages/admin/Services"));
const Barbers = lazy(() => import("@/pages/admin/Barbers"));
const Clients = lazy(() => import("@/pages/admin/Clients"));
const Config = lazy(() => import("@/pages/admin/Config"));
const Appointments = lazy(() => import("@/pages/admin/Appointments"));
const NewAppointment = lazy(() => import("@/pages/admin/NewAppointment"));
const CalendarView = lazy(() => import("@/pages/admin/CalendarView"));
const Inventory = lazy(() => import("@/pages/admin/Inventory"));
const NewPayment = lazy(() => import("@/pages/admin/NewPayment"));
const Tickets = lazy(() => import("@/pages/admin/Tickets"));
const DayClose = lazy(() => import("@/pages/admin/DayClose"));
const CashCut = lazy(() => import("@/pages/admin/CashCut"));
const Commissions = lazy(() => import("@/pages/admin/Commissions"));
const BarbersSummary = lazy(() => import("@/pages/admin/BarbersSummary"));
const Reports = lazy(() => import("@/pages/admin/Reports"));
const Coupons = lazy(() => import("@/pages/admin/Coupons"));
const Blocks = lazy(() => import("@/pages/admin/Blocks"));
const Locations = lazy(() => import("@/pages/admin/Locations"));
const TestimonialsAdmin = lazy(() => import("@/pages/admin/TestimonialsAdmin"));
const Gallery = lazy(() => import("@/pages/admin/Gallery"));
const RemindersToday = lazy(() => import("@/pages/admin/RemindersToday"));
const Activity = lazy(() => import("@/pages/admin/Activity"));
const QRPage = lazy(() => import("@/pages/admin/QRPage"));
const Staff = lazy(() => import("@/pages/admin/Staff"));
const Tutorial = lazy(() => import("@/pages/admin/Tutorial"));
const OwnerManual = lazy(() => import("@/pages/admin/OwnerManual"));
const Account = lazy(() => import("@/pages/admin/Account"));
const Employees = lazy(() => import("@/pages/admin/hr/Employees"));
const Payroll = lazy(() => import("@/pages/admin/hr/Payroll"));
const Investment = lazy(() => import("@/pages/admin/Investment"));
const Assets = lazy(() => import("@/pages/admin/Assets"));
const Finance = lazy(() => import("@/pages/admin/Finance"));
const Rewards = lazy(() => import("@/pages/admin/Rewards"));
const Redemptions = lazy(() => import("@/pages/admin/Redemptions"));
const LoyaltyAudit = lazy(() => import("@/pages/admin/LoyaltyAudit"));
const CortesCierre = lazy(() => import("@/pages/admin/CortesCierre"));

// Barbero (bajo demanda)
const BarberLayout = lazy(() => import("@/components/BarberLayout"));
const BarberDashboard = lazy(() => import("@/pages/barber/BarberDashboard"));
const BarberAppointments = lazy(() => import("@/pages/barber/BarberAppointments"));
const BarberCalendar = lazy(() => import("@/pages/barber/BarberCalendar"));
const BarberBook = lazy(() => import("@/pages/barber/BarberBook"));
const BarberNewPayment = lazy(() => import("@/pages/barber/BarberNewPayment"));
const BarberDayClose = lazy(() => import("@/pages/barber/BarberDayClose"));
const BarberHistory = lazy(() => import("@/pages/barber/BarberHistory"));
const BarberTutorial = lazy(() => import("@/pages/barber/BarberTutorial"));
const BarberManual = lazy(() => import("@/pages/barber/BarberManual"));
const BarberAI = lazy(() => import("@/pages/barber/BarberAI"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0f0f0f]">
      <Loader2 className="w-7 h-7 animate-spin text-[#d4af37]" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Público */}
          <Route path="/" element={<Landing />} />
          <Route path="/book" element={<Book />} />
          <Route path="/booking-success/:id" element={<BookingSuccess />} />
          <Route path="/reschedule/:id" element={<Reschedule />} />
          <Route path="/wallet" element={<Wallet />} />
          <Route path="/aviso-de-privacidad" element={<Privacy />} />
          <Route path="/terminos" element={<Terms />} />
          <Route path="/testimonial" element={<TestimonialPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/mi-cuenta" element={<MiCuenta />} />
          <Route path="/mi-cuenta/referir" element={<Referir />} />

          {/* Admin */}
          <Route path="/admin" element={<ProtectedRoute roles={["admin", "receptionist"]}><AdminLayout /></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="calendar" element={<CalendarView />} />
            <Route path="appointments" element={<Appointments />} />
            <Route path="new-appointment" element={<NewAppointment />} />
            <Route path="clients" element={<Clients />} />
            <Route path="barbers" element={<Barbers />} />
            <Route path="services" element={<Services />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="new-payment" element={<NewPayment />} />
            <Route path="tickets" element={<Tickets />} />
            <Route path="day-close" element={<DayClose />} />
            <Route path="cash-cut" element={<CashCut />} />
            <Route path="commissions" element={<Commissions />} />
            <Route path="barbers-summary" element={<BarbersSummary />} />
            <Route path="reports" element={<Reports />} />
            <Route path="coupons" element={<Coupons />} />
            <Route path="blocks" element={<Blocks />} />
            <Route path="locations" element={<Locations />} />
            <Route path="testimonials" element={<TestimonialsAdmin />} />
            <Route path="gallery" element={<Gallery />} />
            <Route path="reminders-today" element={<RemindersToday />} />
            <Route path="config" element={<Config />} />
            <Route path="activity" element={<Activity />} />
            <Route path="staff" element={<Staff />} />
            <Route path="tutorial" element={<Tutorial />} />
            <Route path="manual" element={<OwnerManual />} />
            <Route path="account" element={<Account />} />
            <Route path="hr/employees" element={<Employees />} />
            <Route path="hr/payroll" element={<Payroll />} />
            <Route path="investment" element={<Investment />} />
            <Route path="assets" element={<Assets />} />
            <Route path="finance" element={<Finance />} />
            <Route path="rewards" element={<Rewards />} />
            <Route path="redemptions" element={<Redemptions />} />
            <Route path="loyalty-audit" element={<LoyaltyAudit />} />
            <Route path="cortes" element={<CortesCierre />} />
            <Route path="qr" element={<QRPage />} />
          </Route>

          {/* Barbero */}
          <Route path="/barber" element={<ProtectedRoute role="barber"><BarberLayout /></ProtectedRoute>}>
            <Route index element={<BarberDashboard />} />
            <Route path="calendar" element={<BarberCalendar />} />
            <Route path="appointments" element={<BarberAppointments />} />
            <Route path="book" element={<BarberBook />} />
            <Route path="new-payment" element={<BarberNewPayment />} />
            <Route path="day-close" element={<BarberDayClose />} />
            <Route path="history" element={<BarberHistory />} />
            <Route path="manual" element={<BarberManual />} />
            <Route path="ai" element={<BarberAI />} />
            <Route path="tutorial" element={<BarberTutorial />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
